import { Parameter } from "@/backend/models/parameter";
import { useParameterStore } from "@/frontend/store/ParameterStore";
import { I18n } from "@/i18n/I18n";
import { CUSTOM_FIELD_TYPES } from "@/types/customFieldTypes";
import { ParameterInputType, ParameterSubjectType, ParameterType } from "@/types/parameterTypes";
import { useRef, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { useShallow } from "zustand/react/shallow";
import { Plus } from "lucide-react";
import { FileDragAndDrop } from "../files/FileDragAndDrop";
import { FileCard } from "../files/FileCard";
import { ConfirmModal } from "../modals/ConfirmModal";
import { useGlobalStore } from "@/frontend/store/GlobalStore";
import { isAbortError } from "@/frontend/utils/isAbortError";

interface RequirementFileAttachmentsProps {
  requirementId: string;
  readOnly?: boolean;
}

export function RequirementFileAttachments({ requirementId, readOnly }: RequirementFileAttachmentsProps) {
  const parameters = useParameterStore(useShallow(state => 
    state.parameters.filter(p => p.subjectId === requirementId && p.subjectType === ParameterSubjectType.REQUIREMENT).sort((a, b) => a.position - b.position)
  ));

  const [showModal, setShowModal] = useState(false);

  // The dialog is rendered here rather than inside the branches below so that it keeps its state -
  // including an upload in progress - when saving the first attachment swaps the empty state out.
  return (
    <div className="mt-4 list-card">
      <p className="mb-0 list-card-title">{I18n.get('attachments')}</p>
      {parameters.length === 0 ? (
        <>
          <div className={`list-card-empty${!readOnly ? ' list-card-empty-with-action' : ''}`}>
            <p className="list-card-empty-text">
              {readOnly ? I18n.get('noAttachments') : I18n.get('addAttachmentHelperText')}
            </p>
          </div>
          {!readOnly && <AddAttachmentButton onClick={() => setShowModal(true)} />}
        </>
      ) : (
        <>
          <div className="attachment-items-container">
            {parameters.map(parameter => (
              <AttachmentItem
                key={parameter.id}
                parameter={parameter}
                readOnly={readOnly}
              />
            ))}
          </div>
          {!readOnly && <AddAttachmentButton onClick={() => setShowModal(true)} />}
        </>
      )}
      {showModal && (
        <AddAttachmentModal requirementId={requirementId} onClose={() => setShowModal(false)} />
      )}
    </div>
  );
}

function AddAttachmentButton({ onClick }: { onClick: () => void; }) {
  return (
    <Button variant="primary" className="fusa-btn-medium-with-icon" onClick={onClick}>
      <Plus size={16} />
      {I18n.get('addAttachment')}
    </Button>
  );
}

function AttachmentItem({ parameter, readOnly }: { 
  parameter: Parameter; 
  readOnly?: boolean;
}) {
  const deleteParameter = useParameterStore(state => state.deleteParameter);
  const file = parameter.fields[0]?.file;

  if(!file) return null;

  return (
    <FileCard file={file} title={parameter.title} readOnly={readOnly} onDelete={async () => await deleteParameter({ id: parameter.id })} />
  );
}

function AddAttachmentModal({ requirementId, onClose }: { requirementId: string; onClose: () => void; }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [titleError, setTitleError] = useState('');
  const [file, setFile] = useState<File | undefined>(undefined);
  const createParameter = useParameterStore(state => state.createParameter);
  const deleteParameter = useParameterStore(state => state.deleteParameter);
  const uploadFile = useGlobalStore(state => state.uploadFile);
  const addErrorToast = useGlobalStore(state => state.addErrorToast);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showCancelUploadConfirm, setShowCancelUploadConfirm] = useState(false);
  // The parameter is created before the file reaches S3, so an upload that never finishes leaves
  // a parameter behind that looks like a successful attachment. Track it so it can be cleaned up.
  const abortUploadRef = useRef<AbortController | undefined>(undefined);
  const pendingParameterIdRef = useRef<string | undefined>(undefined);

  const handleSave = async () => {
    if (!title.trim()) {
      setTitleError(I18n.get('requiredFieldError', { field: I18n.get('title') }));
      return;
    }

    const request = {
      title: title.trim(),
      fieldType: CUSTOM_FIELD_TYPES.File,
      subjectId: requirementId,
      subjectType: ParameterSubjectType.REQUIREMENT,
      type: ParameterType.FIELD,
      inputType: ParameterInputType.FREE_FORM,
      description: description.trim(),
      position: 0,
      fileName: file?.name ?? '',
    };

    setUploading(true);
    const response = await createParameter(request);
    pendingParameterIdRef.current = response.parameter?.id;

    const fileId = response.parameter?.fields[0]?.file?.id;
    if (file && response.uploadUrl && fileId) {
      const abortController = new AbortController();
      abortUploadRef.current = abortController;
      let uploaded = false;
      try {
        uploaded = (await uploadFile(fileId, response.uploadUrl, file, setUploadProgress, abortController.signal)) !== undefined;
      } catch (error) {
        if (isAbortError(error)) {
          // Canceled from the confirmation dialog, which does its own cleanup.
          return;
        }
        addErrorToast(I18n.get('uploadFailed'));
      } finally {
        abortUploadRef.current = undefined;
      }

      if (!uploaded) {
        // The error toast explains the failure. Drop the parameter so the attachment does not show
        // up as if it uploaded, and leave the form open so the file can be uploaded again.
        await discardPendingParameter();
        setUploading(false);
        setUploadProgress(0);
        return;
      }
    }

    pendingParameterIdRef.current = undefined;
    handleClose();
  };

  const discardPendingParameter = async () => {
    const pendingParameterId = pendingParameterIdRef.current;
    pendingParameterIdRef.current = undefined;
    if (pendingParameterId) {
      await deleteParameter({ id: pendingParameterId });
    }
  };

  const cancelUpload = async () => {
    abortUploadRef.current?.abort();
    abortUploadRef.current = undefined;
    await discardPendingParameter();
    handleClose();
  };

  const requestClose = () => {
    if (uploading) {
      setShowCancelUploadConfirm(true);
      return;
    }
    handleClose();
  };

  const handleClose = () => {
    setShowCancelUploadConfirm(false);
    onClose();
  };

  return (
    <>
      <Modal show onHide={requestClose} centered>
        <Modal.Body>
          <Modal.Title className="pb-3">{I18n.get('addAttachment')}</Modal.Title>
          <Form.Group className="mb-3">
            <Form.Label className="form-label-neutral">{I18n.get('title')}</Form.Label>
            <Form.Control
              className="form-control-neutral"
              type="text"
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                setTitleError('');
              }}
              isInvalid={!!titleError}
              placeholder={I18n.get('attachmentTitlePlaceholder')}
            />
            <Form.Control.Feedback type="invalid">
              {titleError}
            </Form.Control.Feedback>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label className="form-label-neutral">{I18n.get('description')} ({I18n.get('optional')})</Form.Label>
            <Form.Control
              className="form-control-neutral"
              as="textarea"
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={I18n.get('attachmentDescriptionPlaceholder')}
            />
          </Form.Group>
          <FileDragAndDrop file={file} setFile={setFile} uploading={uploading} uploadProgress={uploadProgress} />
        </Modal.Body>
        <Modal.Footer>
          <Button className="btn-blue-light fusa-btn-medium" onClick={requestClose}>
            {I18n.get('cancel')}
          </Button>
          <Button className="fusa-btn-medium fusa-btn-primary" onClick={() => void handleSave()} disabled={uploading}>
            {I18n.get('saveAttachment')}
          </Button>
        </Modal.Footer>
      </Modal>
      {showCancelUploadConfirm && (
        <ConfirmModal
          title={I18n.get('cancelUpload')}
          confirmMessage={I18n.get('confirmCancelUpload')}
          confirmText={I18n.get('cancelUpload')}
          cancelText={I18n.get('keepUploading')}
          onConfirm={cancelUpload}
          setShowModal={setShowCancelUploadConfirm}
        />
      )}
    </>
  );
}

